/*
 * Photo eyedropper for color swatches.
 *
 * Goal: every swatch shows the real garment color, taken from the product's
 * packshot image (the photo that shows only the garment), with no wrong-color
 * flicker. Swatches marked [data-swatch-refine] start hidden (CSS) and are
 * revealed only once their final color is set.
 *
 * Strategy per swatch:
 *   1. localStorage cache by product handle  -> instant on repeat views.
 *   2. else fetch /products/<handle>.js, pick the packshot (image with the
 *      least skin), and sample the garment color there. For named color
 *      families (blauw, geel, ...) only pixels in that hue family are used, so
 *      belts / printed tops / skin can't pollute the result.
 *   3. on failure or timeout, reveal with the server-rendered fallback color.
 *
 * Sampling runs lazily (IntersectionObserver) so off-screen cards cost nothing.
 * Swatches with [data-swatch-locked] (client-set custom.swatch_color) are never
 * touched.
 */
(function () {
  if (window.__vdSwatchPhotoColor) return;
  window.__vdSwatchPhotoColor = true;

  var LS = 'vdsw2:';
  var mem = {};      // handle -> {hex, src}
  var inflight = {}; // handle -> Promise (dedupe concurrent work)

  function getRec(h) {
    if (mem[h]) return mem[h];
    try { var v = localStorage.getItem(LS + h); if (v) { mem[h] = JSON.parse(v); return mem[h]; } } catch (e) {}
    return null;
  }
  function setRec(h, rec) { mem[h] = rec; try { localStorage.setItem(LS + h, JSON.stringify(rec)); } catch (e) {} }

  function hexToRgb(hex) {
    hex = (hex || '').trim().replace('#', '');
    if (hex.length === 3) hex = hex.replace(/(.)/g, '$1$1');
    if (hex.length !== 6) return null;
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }
  function rgbToHsv(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn, h = 0;
    if (d) {
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60; if (h < 0) h += 360;
    }
    return [h, mx === 0 ? 0 : d / mx, mx];
  }
  function hueDiff(a, b) { var d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; }
  function median(a) { a.sort(function (x, y) { return x - y; }); return a[Math.floor(a.length / 2)] || 0; }
  function toHex(r, g, b) { return '#' + [r, g, b].map(function (v) { return ('0' + Math.round(v).toString(16)).slice(-2); }).join(''); }

  // Hue-based skin detection (so pastel yellow / beige garments are NOT flagged).
  function isSkin(r, g, b) {
    var hsv = rgbToHsv(r, g, b);
    return hsv[0] >= 8 && hsv[0] <= 38 && hsv[1] >= 0.18 && hsv[1] <= 0.62 && hsv[2] >= 0.4 && hsv[2] <= 0.92;
  }
  function avgRegion(d, W, x0, y0, x1, y1) {
    var r = 0, g = 0, b = 0, n = 0;
    for (var y = y0; y < y1; y++) for (var x = x0; x < x1; x++) { var i = (y * W + x) * 4; r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
    return [r / n, g / n, b / n];
  }

  function sampleImage(img, anchorHue) {
    var W = 100, H = 150;
    var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d'); ctx.drawImage(img, 0, 0, W, H);
    var d;
    try { d = ctx.getImageData(0, 0, W, H).data; } catch (e) { return null; }
    var c = avgRegion;
    var c1 = c(d, W, 0, 0, 7, 7), c2 = c(d, W, W - 7, 0, W, 7), c3 = c(d, W, 0, H - 7, 7, H), c4 = c(d, W, W - 7, H - 7, W, H);
    var bg = [(c1[0] + c2[0] + c3[0] + c4[0]) / 4, (c1[1] + c2[1] + c3[1] + c4[1]) / 4, (c1[2] + c2[2] + c3[2] + c4[2]) / 4];
    var garment = 0, skin = 0, rs = [], gs = [], bs = [];
    for (var y = 0; y < H; y++) for (var x = 0; x < W; x++) {
      var i = (y * W + x) * 4, r = d[i], g = d[i + 1], b = d[i + 2];
      var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      if (Math.abs(r - bg[0]) + Math.abs(g - bg[1]) + Math.abs(b - bg[2]) < 40) continue;
      if (mx < 22 || mn > 244) continue;
      garment++;
      if (isSkin(r, g, b)) { skin++; continue; }
      if (anchorHue !== null) {
        var hsv = rgbToHsv(r, g, b);
        if (hsv[1] < 0.10) continue;                       // ignore near-grey (belts, shadow)
        if (hueDiff(hsv[0], anchorHue) > 34) continue;     // keep only the title's color family
      }
      rs.push(r); gs.push(g); bs.push(b);
    }
    if (garment < 200) return null;
    var need = anchorHue !== null ? 40 : 80;
    var skinFrac = skin / garment;
    if (rs.length < need) return { hex: null, skinFrac: skinFrac, n: rs.length };
    var R = median(rs), G = median(gs), B = median(bs);
    var m = Math.max(R, G, B);
    if (m > 0 && m < 60) { var k = 78 / m; R *= k; G *= k; B *= k; } // lift very dark so the hue stays visible
    return { hex: toHex(R, G, B), skinFrac: skinFrac, n: rs.length };
  }

  function loadImage(src) {
    return new Promise(function (res) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { res(im); };
      im.onerror = function () { res(null); };
      im.src = src;
    });
  }
  function normalize(src) {
    if (src && src.indexOf('//') === 0) src = 'https:' + src;
    return src ? src.split('?')[0] + '?width=260' : src;
  }

  // Fetch the product, find its packshot (least skin) and sample the garment color there.
  // Returns {hex, src}: hex = solid garment color, src = packshot image url (for print patches).
  function computeProduct(handle, anchorHue) {
    if (inflight[handle]) return inflight[handle];
    var p = fetch('/products/' + handle + '.js').then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
      if (!j || !j.images) return { hex: null, src: null };
      var imgs = j.images.slice(0, 6);
      var idx = 0, best = null;
      return (function next() {
        if (idx >= imgs.length) return best ? { hex: best.hex, src: best.src } : { hex: null, src: null };
        var src = normalize(imgs[idx++]);
        return loadImage(src).then(function (im) {
          if (im) {
            var res = sampleImage(im, anchorHue);
            if (res) {
              res.src = src;
              if (!best || res.skinFrac < best.skinFrac - 0.05 ||
                 (Math.abs(res.skinFrac - best.skinFrac) < 0.05 && (res.n || 0) > (best.n || 0))) best = res;
              if (best.skinFrac < 0.04 && (best.n || 0) > 250) return { hex: best.hex, src: best.src };
            }
          }
          return next();
        });
      })();
    }).catch(function () { return { hex: null, src: null }; });
    inflight[handle] = p;
    return p;
  }

  function reveal(el) { el.setAttribute('data-swatch-done', '1'); }
  function applyColor(el, hex) {
    el.style.setProperty('--swatch-background-color', hex);
    el.style.setProperty('--swatch-background-image', 'none');
    if (el.classList.contains('card-color-swatches__item')) el.style.backgroundColor = hex;
    reveal(el);
  }
  function applyPrint(el, src) {
    el.style.setProperty('--swatch-background-color', 'transparent');
    el.style.setProperty('--swatch-background-image', 'url(' + src + ')');
    if (el.classList.contains('card-color-swatches__item')) {
      el.style.backgroundImage = 'url(' + src + ')';
      el.style.backgroundColor = 'transparent';
    }
    reveal(el);
  }

  function process(el) {
    if (el.getAttribute('data-swatch-proc')) return;
    el.setAttribute('data-swatch-proc', '1');

    var href = el.getAttribute('href') || '';
    var handle = href.split('?')[0].replace(/\/$/, '').split('/').pop();
    if (!handle) { reveal(el); return; }

    var isPrint = el.hasAttribute('data-swatch-print');
    var anchorless = el.hasAttribute('data-swatch-anchorless');
    var anchorRgb = (isPrint || anchorless) ? null : hexToRgb(el.style.getPropertyValue('--swatch-background-color'));
    var anchorHue = anchorRgb ? rgbToHsv(anchorRgb[0], anchorRgb[1], anchorRgb[2])[0] : null;

    var apply = function (rec) {
      if (isPrint) { rec.src ? applyPrint(el, rec.src) : reveal(el); return; }
      if (!rec.hex) { reveal(el); return; }
      if (!anchorless && anchorHue !== null) {
        var hh = rgbToHsv.apply(null, hexToRgb(rec.hex))[0];
        if (hueDiff(hh, anchorHue) > 50) { reveal(el); return; } // sample disagrees with title → keep fallback
      }
      applyColor(el, rec.hex);
    };

    var cached = getRec(handle);
    if (cached) { apply(cached); return; }

    var safety = setTimeout(function () { reveal(el); }, 5000); // never leave it stuck
    computeProduct(handle, isPrint ? null : anchorHue).then(function (rec) {
      clearTimeout(safety);
      setRec(handle, rec);
      apply(rec);
    }).catch(function () { clearTimeout(safety); reveal(el); });
  }

  // Lazy: only sample swatches near the viewport.
  var io = window.IntersectionObserver ? new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) { var el = entries[i].target; io.unobserve(el); process(el); }
  }, { rootMargin: '200px' }) : null;

  function observe(el) {
    if (el.getAttribute('data-swatch-proc') || el.getAttribute('data-swatch-seen')) return;
    el.setAttribute('data-swatch-seen', '1');
    if (io) io.observe(el); else process(el);
  }
  function scan(root) {
    var sel = '[data-swatch-refine]:not([data-swatch-locked]):not([data-swatch-seen]),'
            + '[data-swatch-print]:not([data-swatch-locked]):not([data-swatch-seen])';
    var els = (root || document).querySelectorAll(sel);
    for (var i = 0; i < els.length; i++) observe(els[i]);
  }

  var scheduled = false;
  function scheduleScan() {
    if (scheduled) return; scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () { scheduled = false; scan(); }, 0);
  }
  if (window.MutationObserver) {
    new MutationObserver(function (m) {
      for (var i = 0; i < m.length; i++) if (m[i].addedNodes && m[i].addedNodes.length) { scheduleScan(); break; }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();
  window.vdSwatchRecolor = scan;
})();
